import React, { useMemo } from 'react';
import { Form } from 'antd';
import {
	AllianceAccountingCodes,
	FeatureFlags,
	NavigareAccountingCodes,
} from '@shared/utils/constants';
import {
	getMeratusAccountCodeOptions,
} from '@shared/utils/meratus';
import useFeatureFlag from '@client/utils/hooks/useFeatureFlag';
import Select from './Select';

type MeratusAccountCodeConfig = Record<string, {
	label: string;
	accountCode: string;
}>;

type AccountCodeOverrideFormItemProps = {
	meratusAccountCodes?: MeratusAccountCodeConfig;
	required?: boolean;
	disabled?: boolean;
};

const AccountCodeOverrideFormItem = ({
	meratusAccountCodes,
	required = false,
	disabled = false,
}: AccountCodeOverrideFormItemProps) => {
	const isNavigare = useFeatureFlag(FeatureFlags.NAVIGARE_ACCOUNTING);
	const isAlliance = useFeatureFlag(FeatureFlags.ALLIANCE_ACCOUNTING);
	const isMeratus = useFeatureFlag(FeatureFlags.MERATUS);

	const options = useMemo(() => {
		if (isMeratus && meratusAccountCodes != null) {
			return getMeratusAccountCodeOptions(meratusAccountCodes);
		}

		const codesToUse = isAlliance ?
			AllianceAccountingCodes :
			(isNavigare ? NavigareAccountingCodes : null);

		if (codesToUse == null) {
			return [];
		}

		return Object.keys(codesToUse).map((code) => ({
			label: `${codesToUse[code as keyof typeof codesToUse]} (${code})`,
			value: code,
		}));
	}, [isAlliance, isMeratus, isNavigare, meratusAccountCodes]);

	if (options.length === 0) {
		return null;
	}

	return (
		<Form.Item
			name="accountCodeOverride"
			label="Account"
			rules={required ? [{ required: true, message: 'Account is required' }] : []}
		>
			<Select
				placeholder="Select account"
				options={options}
				allowClear={!required}
				showSearch
				disabled={disabled}
			/>
		</Form.Item>
	);
};

export default AccountCodeOverrideFormItem;
